import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cable, Cpu, MonitorCheck } from "lucide-react";
import PageHero from "../components/common/PageHero";
import { Reveal, SectionHeading } from "../components/common/Section";
import SignalPath from "../components/diagrams/SignalPath";
import Lifecycle from "../components/diagrams/Lifecycle";
import InteractionDemo from "../components/diagrams/InteractionDemo";
import { Button } from "../components/ui/button";
import { ONBOARDING_STEPS } from "../data/content";

const compIcons = { "Bifr\u00f6st Relay": Cable, "Aegis Engine": Cpu, "Heimdall Console": MonitorCheck };
const comps = [
  { name: "Bifr\u00f6st Relay", role: "Connects approved systems, normalises, encrypts and routes messages." },
  { name: "Aegis Engine", role: "Detects, classifies, evaluates policy and applies the configured action." },
  { name: "Heimdall Console", role: "Alert triage, incident review, policy management and reporting." },
];

export default function HowItWorks() {
  useEffect(() => {
    document.title = "How It Works | norse3";
  }, []);

  return (
    <div>
      <PageHero
        eyebrow="How it works"
        title="A control layer that sits between your conversations and your models."
        subtitle="norse3 inspects inbound prompts and outbound responses, applies your policies and creates evidence — while people stay in control of decisions."
      >
        <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
        </Button>
      </PageHero>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SignalPath /></Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="The lifecycle"
              title="Identify → decide → act → evidence → review."
              subtitle="Every high-risk interaction follows the same path, so behaviour is consistent and explainable."
            />
          </Reveal>
          <Reveal className="mt-10" delay={100}><Lifecycle /></Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Three connected components"
              title="Relay, engine and console."
              subtitle="Each part has a clear job. Together they form one control and evidence path."
            />
          </Reveal>
          <Reveal className="mt-10 grid gap-4 lg:grid-cols-3" delay={100}>
            {comps.map((c) => {
              const Icon = compIcons[c.name];
              return (
                <div key={c.name} className="rounded-xl border border-white/10 bg-navy-900/60 p-6">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-foreground">{c.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.role}</p>
                </div>
              );
            })}
          </Reveal>
          <div className="mt-6">
            <Link to="/platform" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
              See the full platform detail <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="In action"
              title="What happens to an interaction."
              subtitle="Three illustrative scenarios. Demonstrations only — not records of real customers."
            />
          </Reveal>
          <Reveal className="mt-10" delay={100}><InteractionDemo /></Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Getting started"
              title="From discovery to ongoing monitoring."
              subtitle="A structured path that is validated with your teams at each stage. We do not promise deployment in minutes."
            />
          </Reveal>
          <Reveal className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" delay={100}>
            {ONBOARDING_STEPS.map((s, i) => (
              <div key={s.step} className="rounded-xl border border-white/10 bg-navy-900/60 p-6">
                <span className="text-xs font-mono text-primary/70">Step {i + 1}</span>
                <h3 className="mt-2 text-lg font-semibold text-foreground">{s.step}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
